import { memo, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeftRight,
  Check,
  ChevronRight,
  CircleHelp,
  Clock3,
  Crown,
  DoorClosed,
  DoorOpen,
  Eye,
  Grid3X3,
  Home,
  Layers3,
  Maximize2,
  Medal,
  MousePointer2,
  RotateCcw,
  X,
  Trash2,
  Trophy,
  UserRound,
  WifiOff,
} from "lucide-react";
import type { ConnectionState } from "../../hooks/useSkyjoGame";
import type {
  CardView,
  DrawSource,
  GamePlayer,
  GameState,
  PlayerAccount,
  RoundScore,
} from "../../types/protocol";
import { CardBackMark } from "../game/CardBackMark";
import { CardFlightLayer } from "../game/CardFlightLayer";
import { OpponentInspector } from "../game/OpponentInspector";
import { AvatarPhoto } from "../ui/Avatar";
import { Brand } from "../ui/Brand";
import { DangerAction } from "../ui/DangerAction";
import {
  arrivalOf,
  coversOf,
  discardTimeline,
  EMPTY_PLAN,
  landsInOwnHand,
  landsOnDiscard,
  localDiscardGroup,
  localReclaimGroup,
  planCardMotions,
  type DiscardEvent,
  type DiscardTopStep,
  type FlightGroup,
  type PickupEvent,
} from "../../lib/card-motion";
import { landingAfter, stripScrollTarget, type ActiveCover } from "../../lib/card-flight";
import { useBackdropHold } from "../../hooks/useBackdropHold";
import { useCheatPeek } from "../../hooks/useCheatPeek";

const valueLabel = (value: number) => (value < 0 ? `−${Math.abs(value)}` : String(value));
const scoreDeltaLabel = (value: number) => (value > 0 ? `+${value}` : valueLabel(value));

/**
 * Une fin de manche retourne d'un coup toutes les cartes encore cachées. Le
 * tableau de score les recouvrirait avant qu'on ait pu les lire : on lui laisse
 * le temps des retournements, plus une seconde pour regarder les grilles.
 */
const ROUND_SUMMARY_DELAY_MS = 1_800;

const valueTone = (value: number | null) => {
  if (value === null) return "hidden";
  if (value < 0) return "negative";
  if (value === 0) return "zero";
  if (value <= 4) return "low";
  if (value <= 8) return "mid";
  return "high";
};

/** Teinte du compte quand il y en a un, sinon la couleur dérivée du siège. */
const avatarStyle = (account: PlayerAccount, seat: number): CSSProperties =>
  account
    ? ({ "--avatar-gradient": `var(--avatar-${account.avatarId})` } as CSSProperties)
    : ({ "--avatar-hue": `${(seat * 57 + 242) % 360}` } as CSSProperties);

/** Le décor d'une face retournée : les chiffres d'angle, la valeur, le motif. */
const CardFaceContent = ({ value }: { value: number | null }) => (
  <>
    <small>{value !== null ? valueLabel(value) : ""}</small>
    <strong>{value !== null ? valueLabel(value) : ""}</strong>
    <span className="card-value-pattern" aria-hidden="true"><i /><i /><i /></span>
    <small>{value !== null ? valueLabel(value) : ""}</small>
  </>
);

/**
 * Cache posé sur une case pendant qu'une carte la quitte ou s'y pose. Il tient
 * la case dans son état d'avant le coup : l'instantané du serveur montre déjà
 * le résultat, et sans lui la carte volerait vers une case déjà retournée.
 *
 * Sur une case de plateau, `detailed` lui fait rendre la face entière plutôt
 * qu'un aplat. Une colonne qui s'aligne est le seul moment où un cache porte
 * une valeur : en aplat, les trois cartes perdaient trame, chiffres d'angle et
 * motif juste avant de s'envoler — à l'instant même que le temps de lecture est
 * censé offrir. Les mini-grilles gardent l'aplat, qui y copie fidèlement leur
 * face.
 */
const CardCoverFace = ({
  cover,
  className,
  detailed = false,
}: { cover: ActiveCover; className: string; detailed?: boolean }) => {
  const value = cover.face?.value ?? null;
  const tone = valueTone(value);
  // La teinte passe par le modificateur de la case, et non par celui du cache :
  // c'est lui que la feuille associe déjà à la trame et au motif de la face.
  const skin = cover.face
    ? detailed
      ? ` number-card--${tone}`
      : ` card-cover--${tone}`
    : " card-cover--vacant";

  return (
    <motion.span
      key={cover.key}
      className={`${className} card-cover${skin}`}
      initial={{ opacity: 1 }}
      animate={{ opacity: cover.active ? 0 : 1 }}
      transition={cover.active ? { delay: cover.fadesAt, duration: 0.16 } : { duration: 0 }}
      aria-hidden="true"
    >
      {value !== null &&
        (detailed ? (
          <span className="number-card__front"><CardFaceContent value={value} /></span>
        ) : (
          <strong>{valueLabel(value)}</strong>
        ))}
    </motion.span>
  );
};

interface NumberCardProps {
  /* L'état et la valeur voyagent séparément : l'objet `card` sort d'un
     instantané fraîchement désérialisé et change d'identité à chaque message du
     serveur, ce qui empêcherait toute comparaison superficielle de mordre. */
  cardState: CardView["state"];
  cardValue: number | null;
  /**
   * Valeur montrée le temps d'un coup d'œil, et `null` le reste du temps. Elle
   * ne touche ni à l'état de la case ni à ce qu'on peut y faire : seule la face
   * affichée change.
   */
  peekValue?: number | null;
  index: number;
  interactive?: boolean;
  selected?: boolean;
  actionHint?: "swap" | "reveal";
  cover?: ActiveCover | null;
  /**
   * Suit toutes les bascules qui changent la hauteur offerte à la grille. Le
   * repère voyage en propriété, et pas seulement en dépendance de mesure : sans
   * cela la comparaison de `memo` bloquerait le rendu des cases inchangées, et
   * elles ne se redimensionneraient pas davantage.
   */
  layoutKey: string;
  onSelect?: (index: number) => void;
  label?: string;
}

const NumberCard = memo(({
  cardState,
  cardValue,
  peekValue = null,
  index,
  interactive = false,
  selected = false,
  actionHint,
  cover,
  layoutKey,
  onSelect,
  label,
}: NumberCardProps) => {
  const reducedMotion = useReducedMotion();
  const hidden = cardState === "hidden";
  const removed = cardState === "removed";
  // Le coup d'œil ne retourne que ce qui est encore caché, et n'a rien à dire
  // d'une case retirée ni d'une carte déjà sur la table.
  const peeked = hidden && peekValue !== null;
  const shownValue = peeked ? peekValue : cardValue;
  const tone = valueTone(shownValue);

  return (
    <motion.button
      className={`number-card number-card--${tone}${hidden ? " is-hidden" : ""}${removed ? " is-removed" : ""}${selected ? " is-selected" : ""}${actionHint ? ` is-${actionHint}-target` : ""}${cover ? " is-covered" : ""}`}
      data-card-index={index}
      type="button"
      disabled={!interactive || removed}
      onClick={() => onSelect?.(index)}
      aria-pressed={selected || undefined}
      aria-label={
        label ??
        (removed
          ? `Emplacement ${index + 1}, colonne retirée`
          : hidden
            ? `Carte ${index + 1}, face cachée`
            : `Carte ${index + 1}, valeur ${cardValue}`)
      }
      whileHover={!reducedMotion && interactive && !removed ? { y: -5, scale: 1.025 } : undefined}
      whileTap={!reducedMotion && interactive && !removed ? { scale: 0.96 } : undefined}
      // Les cases changent de taille quand le panneau du haut change de
      // hauteur : au moment où le choix d'ouverture est verrouillé, puis quand
      // la pioche remplace ce panneau. `layoutDependency` réserve la mesure à
      // ces instants ; sans elle, framer remesurait toute la grille à chaque
      // rendu, pour une géométrie qui n'a pas bougé.
      layout
      layoutDependency={layoutKey}
    >
      <AnimatePresence initial={false} mode="wait">
        {removed ? (
          <motion.span className="number-card__removed" key="removed" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <i /><i />
          </motion.span>
        ) : hidden && !peeked ? (
          <motion.span
            className="number-card__back"
            key="back"
            initial={reducedMotion ? undefined : { rotateY: -90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={reducedMotion ? undefined : { rotateY: 90, opacity: 0 }}
          >
            <CardBackMark />
          </motion.span>
        ) : (
          <motion.span
            className="number-card__front"
            key={`value-${shownValue}`}
            initial={reducedMotion ? undefined : { rotateY: -90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            // Le retour en arrière n'appartient qu'à une case encore cachée :
            // une carte réellement retournée ne redevient jamais un dos, et lui
            // poser une sortie retarderait le passage à l'emplacement retiré.
            exit={hidden && !reducedMotion ? { rotateY: 90, opacity: 0 } : undefined}
            // La colonne se retourne en cascade à l'aller. Le retard vit sur
            // l'entrée et non sur la sortie : une sortie garde les propriétés
            // du rendu précédent, où le coup d'œil n'avait pas encore commencé.
            transition={peeked ? { delay: index * 0.022 } : undefined}
          >
            <CardFaceContent value={shownValue} />
          </motion.span>
        )}
      </AnimatePresence>
      {cover && <CardCoverFace cover={cover} className="number-card__cover" detailed />}
      {/* La pastille porte seule la consigne : elle remplace le bandeau qui
          coûtait une bande de hauteur sous la pioche. Son apparition est une
          image clé CSS : en framer, ces douze pastilles montaient d'un coup
          autant d'éléments animés, chacun avec son ressort à éteindre. */}
      {actionHint && !cover && (
        <span className="card-action-cue" aria-hidden="true">
          {actionHint === "reveal" ? (
            <><Eye size={13} /> Révéler</>
          ) : (
            <><ArrowLeftRight size={13} /> Échanger</>
          )}
        </span>
      )}
      {/* Sans `layoutId` : partagé, il rouvrait à lui seul la passe de mesure de
          framer sur tout l'écran, alors que la pastille ne se déplace pas d'une
          case à l'autre — elle apparaît et disparaît sur place. */}
      {selected && <span className="card-selected-check"><Check size={14} /></span>}
    </motion.button>
  );
});

/**
 * Une case de mini-grille. Trente-six d'entre elles vivent en même temps sur une
 * table à quatre : mémoïsée sur des valeurs simples, seule celle qui change
 * vraiment se rend à nouveau quand un instantané arrive.
 */
const MiniCard = memo(({
  cardState,
  cardValue,
  index,
  cover,
}: {
  cardState: CardView["state"];
  cardValue: number | null;
  index: number;
  cover: ActiveCover | null;
}) => {
  const reducedMotion = useReducedMotion();

  return (
    // La cellule ne bouge jamais : elle garde sa place dans la grille et
    // l'ancre `data-card-index` dont dépendent les vols. Seule la face
    // pivote, pour qu'un échange adverse se lise comme un retournement et
    // non comme une valeur qui change d'un coup.
    <span
      className="mini-card"
      data-card-index={index}
      aria-label={cardState === "revealed" ? `Carte ${cardValue}` : "Carte cachée"}
    >
      <AnimatePresence initial={false} mode="wait">
        <motion.span
          className={`mini-card__face mini-card--${cardState} mini-card--${valueTone(cardValue)}`}
          key={`${cardState}:${cardValue ?? ""}`}
          initial={reducedMotion ? undefined : { rotateY: -90, opacity: 0 }}
          animate={{ rotateY: 0, opacity: 1 }}
          exit={reducedMotion ? undefined : { rotateY: 90, opacity: 0 }}
          transition={{ duration: 0.16, ease: "easeOut" }}
        >
          {cardState === "revealed" && cardValue !== null ? valueLabel(cardValue) : ""}
        </motion.span>
      </AnimatePresence>
      {cover && <CardCoverFace cover={cover} className="mini-card__cover" />}
    </span>
  );
});

const MiniGrid = ({
  player,
  covers,
}: {
  player: GamePlayer;
  covers: Map<string, ActiveCover>;
}) => (
  <div className="mini-grid" aria-label={`Grille de ${player.name}`}>
    {player.grid.map((card, index) => (
      <MiniCard
        key={index}
        index={index}
        cardState={card.state}
        cardValue={card.value}
        cover={covers.get(`${player.id}:${index}`) ?? null}
      />
    ))}
  </div>
);

const OpponentBoard = memo(({
  player,
  active,
  holding,
  receiving,
  discarding,
  covers,
  onInspect,
}: {
  player: GamePlayer;
  active: boolean;
  holding: PickupEvent | null;
  receiving: boolean;
  discarding: DiscardEvent | null;
  covers: Map<string, ActiveCover>;
  onInspect: (playerId: string) => void;
}) => (
  <motion.article
    className={`opponent-board glass-panel is-inspectable${active ? " is-active" : ""}${holding ? " has-held-card" : ""}${receiving ? " is-receiving-card" : ""}${discarding ? " is-sending-discard" : ""}${!player.connected ? " is-away" : ""}${player.hasLeft ? " is-gone" : ""}`}
    data-player-id={player.id}
    role="button"
    tabIndex={0}
    aria-label={`Agrandir le plateau de ${player.name}`}
    aria-haspopup="dialog"
    onClick={() => onInspect(player.id)}
    // Un élément rendu focusable par tabindex prend le focus au clic comme au
    // doigt, et les moteurs ne s'accordent pas sur le :focus-visible qui en
    // découle. On refuse la prise de focus au pointeur : le contour ambre ne
    // peut plus venir que d'une tabulation, et le clic passe quand même.
    onMouseDown={(event) => event.preventDefault()}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onInspect(player.id);
      }
    }}
    // Pas de `layout` ici. La liste des joueurs est figée au démarrage de la
    // partie et un joueur qui s'en va garde son siège : la bande ne se
    // réorganise jamais, donc la projection de framer n'aurait rien à animer.
    // Elle n'était pas seulement inutile, elle nuisait : sur un plateau tapé,
    // elle reprend la main sur `transform` — celui-là même que `whileTap`
    // anime — et la mesure déclenchée par l'ouverture de l'inspecteur faisait
    // sauter le plateau d'une image. Le scintillement ne se voyait qu'au doigt,
    // seul geste qui arme `whileTap` au moment où la modale se monte.
    whileTap={{ scale: 0.98 }}
  >
    <header>
      <span
        className={`tiny-avatar${player.account ? " tiny-avatar--account" : ""}`}
        style={avatarStyle(player.account, player.seat)}
      >
        {player.name.slice(0, 1).toUpperCase()}
        <AvatarPhoto profile={player.account} />
      </span>
      <span><strong>{player.name}</strong><small>{player.totalScore} pts</small></span>
      {active && <span className="turn-indicator" title="À son tour"><i /></span>}
    </header>
    <MiniGrid player={player} covers={covers} />
    <AnimatePresence>
      {holding && (
        <motion.span
          className={`opponent-held-card opponent-held-card--${holding.source}${discarding?.origin === "held" ? " is-departing" : ""}`}
          title={`${player.name} a pris ${holding.source === "deck" ? "la pioche" : "la défausse"}`}
          initial={{ opacity: 0, scale: 0.65, y: 7 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.72, y: 4 }}
          // La pastille ne se pose qu'au moment où la carte y atterrit.
          transition={{ duration: 0.2, delay: receiving ? landingAfter("opponent") : 0 }}
          aria-hidden="true"
        >
          <span
            className={`opponent-held-card__card${holding.source === "deck" || holding.publicValue === null
              ? " is-hidden"
              : ` is-${valueTone(holding.publicValue)}`}`}
          >
            {holding.source === "deck" || holding.publicValue === null ? (
              <CardBackMark />
            ) : (
              <strong>{valueLabel(holding.publicValue)}</strong>
            )}
          </span>
          <span className="opponent-held-card__source">
            {holding.source === "deck" ? <Layers3 size={10} /> : <RotateCcw size={10} />}
            {holding.source === "deck" ? "Pioche" : "Défausse"}
          </span>
        </motion.span>
      )}
    </AnimatePresence>
    <span className="opponent-board__expand" aria-hidden="true"><Maximize2 size={11} /></span>
    {/* Parti et déconnecté n'ont pas la même conséquence : l'un ne reviendra
        jamais et son tour est sauté, l'autre garde sa place. */}
    {player.hasLeft ? (
      <span className="away-label away-label--gone"><DoorOpen size={11} /> a quitté</span>
    ) : !player.connected ? (
      <span className="away-label"><WifiOff size={11} /> absent</span>
    ) : null}
  </motion.article>
));

const PileCard = ({ value, hidden }: { value: number | null; hidden?: boolean }) => (
  <span
    className={`pile-card${hidden ? " pile-card--back" : ` pile-card--${valueTone(value)}`}`}
    aria-hidden="true"
  >
    {hidden ? <CardBackMark /> : value !== null ? <strong>{valueLabel(value)}</strong> : <span>—</span>}
  </span>
);

/**
 * La carte tenue n'apparaît qu'à l'instant où son vol la dépose dans la main.
 * Le retard est figé au montage : il vaut pour cette carte-là, et ne doit pas
 * se réajuster aux rendus qui suivent.
 */
const HeldCard = ({ value, arrival }: { value: number; arrival: number | null }) => {
  const [delay] = useState(arrival ?? 0);

  return (
    <motion.div
      className={`held-card held-card--${valueTone(value)}`}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.1 } }}
      transition={{ delay, duration: 0.18, ease: "easeOut" }}
    >
      <span>Ta carte</span>
      <strong>{valueLabel(value)}</strong>
      <i className="card-value-pattern"><i /><i /><i /></i>
    </motion.div>
  );
};

interface DrawZoneProps {
  game: GameState;
  canDraw: boolean;
  canPlace: boolean;
  canRevealHidden: boolean;
  revealAfterDiscard: boolean;
  canRevealAfterDiscard: boolean;
  isSelfTurn: boolean;
  prompt: string;
  pendingAction: string | null;
  interactionLocked: boolean;
  /** Pile d'où décolle la carte en vol : elle s'illumine le temps du départ. */
  flightSource: DrawSource | null;
  /** Secondes avant qu'une carte ne se pose sur la défausse ; `null` si aucune. */
  discardArrival: number | null;
  /** Ce que la défausse montre pendant les vols qui la visent. */
  discardSteps: DiscardTopStep[];
  /** Retard d'apparition de la carte tenue, le temps qu'elle arrive en main. */
  handArrival: number | null;
  incomingDiscard: DiscardEvent | null;
  onDiscardDrawn: () => Promise<boolean>;
  onReclaimDiscard: () => Promise<boolean>;
  onDraw: (source: DrawSource) => Promise<boolean>;
}

const DrawZone = ({
  game,
  canDraw,
  canPlace,
  canRevealHidden,
  revealAfterDiscard,
  canRevealAfterDiscard,
  isSelfTurn,
  prompt,
  pendingAction,
  interactionLocked,
  flightSource,
  discardArrival,
  discardSteps,
  handArrival,
  incomingDiscard,
  onDiscardDrawn,
  onReclaimDiscard,
  onDraw,
}: DrawZoneProps) => {
  const hasHeldCard = game.heldCard !== null;
  const canReclaimNow = revealAfterDiscard && game.canReclaimDiscard;
  const canStageDiscard =
    !interactionLocked &&
    canPlace &&
    canRevealHidden &&
    hasHeldCard &&
    game.canDiscardHeld &&
    pendingAction === null;
  const discardDisabled =
    interactionLocked ||
    (revealAfterDiscard
      ? !canRevealAfterDiscard || !canReclaimNow || pendingAction !== null
      : hasHeldCard
        ? pendingAction !== null || !canPlace || !canStageDiscard
        : !canDraw || pendingAction !== null || game.discardTop === null);

  // Le sommet montré n'est pas toujours celui de l'état : une carte encore en
  // vol ne doit pas être déjà posée sur la pile qu'elle rejoint. Elle s'y
  // découvre à la seconde de son atterrissage, sous la carte qui s'y efface.
  const [discardStep, setDiscardStep] = useState(0);
  const discardStepsKey = discardSteps.map((step) => `${step.at}:${step.value}`).join("|");

  useEffect(() => {
    setDiscardStep(0);
    const timers = discardSteps
      .slice(1)
      .map((step, index) => window.setTimeout(() => setDiscardStep(index + 1), step.at * 1000));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
    // La clé résume la suite d'étapes, reconstruite à chaque rendu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discardStepsKey]);

  const shownDiscardTop =
    discardSteps.length > 0
      ? discardSteps[Math.min(discardStep, discardSteps.length - 1)].value
      : game.discardTop;

  return (
    <section className="draw-zone glass-panel" aria-label="Pioche et défausse">
      <button
        className={`pile-button pile-button--deck${flightSource === "deck" ? " is-source" : ""}`}
        data-draw-source="deck"
        type="button"
        disabled={interactionLocked || !canDraw || pendingAction !== null}
        onClick={() => void onDraw("deck")}
        aria-label={`Piocher une carte, ${game.deckCount} restantes`}
      >
        <span className="pile-stack" aria-hidden="true"><i /><i /></span>
        <PileCard value={null} hidden />
        <span className="pile-caption"><Layers3 size={14} /> Pioche <small>{game.deckCount}</small></span>
      </button>

      <div className="held-slot" aria-live="polite">
        {/* Repère permanent : la carte tenue quitte le DOM au moment même où
            elle s'envole, et un vol doit encore pouvoir viser sa place. */}
        <i className="held-anchor" aria-hidden="true" />
        <AnimatePresence mode="sync">
          {game.heldCard !== null ? (
            <HeldCard
              key={`held-${game.round}-${game.currentPlayerId}-${game.drawnFrom}-${game.heldCard}`}
              value={game.heldCard}
              arrival={handArrival}
            />
          ) : revealAfterDiscard ? (
            <motion.div
              className="draw-prompt is-current is-reveal-step"
              key="reveal-step"
              initial={{ opacity: 0, scale: .92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: .92 }}
              role="status"
            >
              <Eye size={24} />
              <span>Révèle une carte cachée</span>
            </motion.div>
          ) : (
            <motion.div
              className={`draw-prompt${isSelfTurn ? " is-current" : ""}`}
              key={`prompt-${prompt}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              role="status"
            >
              {incomingDiscard ? (
                <Trash2 size={20} />
              ) : isSelfTurn ? (
                <MousePointer2 size={20} />
              ) : flightSource === "deck" || game.drawnFrom === "deck" ? (
                <Layers3 size={20} />
              ) : flightSource === "discard" || game.drawnFrom === "discard" ? (
                <RotateCcw size={20} />
              ) : (
                <Clock3 size={20} />
              )}
              <span>{prompt}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <button
        className={`pile-button pile-button--discard${canStageDiscard ? " is-drop-target" : ""}${revealAfterDiscard ? " is-staged" : ""}${flightSource === "discard" ? " is-source" : ""}${discardArrival !== null ? " is-landing" : ""}`}
        style={
          discardArrival !== null
            ? ({ "--flight-landing": `${discardArrival}s` } as CSSProperties)
            : undefined
        }
        data-draw-source="discard"
        type="button"
        disabled={discardDisabled}
        onClick={() => {
          if (revealAfterDiscard) {
            if (canReclaimNow) void onReclaimDiscard();
            return;
          }
          if (hasHeldCard) {
            if (canStageDiscard) void onDiscardDrawn();
            return;
          }
          void onDraw("discard");
        }}
        aria-label={
          canReclaimNow && game.discardTop !== null
            ? `Reprendre le ${valueLabel(game.discardTop)}`
            : revealAfterDiscard
              ? "Carte défaussée, révèle une carte cachée"
              : canStageDiscard && game.heldCard !== null
                ? `Défausser le ${valueLabel(game.heldCard)}, puis révéler une carte cachée`
                : game.discardTop === null
                  ? "Défausse vide"
                  : `Prendre le ${game.discardTop} de la défausse`
        }
      >
        <PileCard value={shownDiscardTop} />
        <span className="pile-caption">
          {canReclaimNow ? <RotateCcw size={14} /> : revealAfterDiscard ? <Eye size={14} /> : canStageDiscard ? <Trash2 size={14} /> : <RotateCcw size={14} />}
          {canReclaimNow && game.discardTop !== null
            ? `Reprendre le ${valueLabel(game.discardTop)}`
            : revealAfterDiscard
              ? "Carte défaussée"
              : canStageDiscard && game.heldCard !== null
                ? `Défausser le ${valueLabel(game.heldCard)}`
                : "Défausse"}
        </span>
      </button>

    </section>
  );
};

const SetupPanel = ({
  selected,
  submitted,
  pending,
  canSubmit,
  onConfirm,
}: {
  selected: number[];
  submitted: boolean;
  pending: boolean;
  canSubmit: boolean;
  onConfirm: () => void;
}) => (
  <motion.aside className="setup-panel glass-panel" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
    {submitted ? (
      <>
        <span className="setup-panel__icon is-done"><Check size={20} /></span>
        <div><strong>Cartes révélées</strong><small>On attend les choix des autres joueurs…</small></div>
        <span className="waiting-dots" aria-hidden="true"><i /><i /><i /></span>
      </>
    ) : (
      <>
        <span className="setup-panel__icon"><Eye size={20} /></span>
        <div>
          <strong>Révèle deux cartes</strong>
          <small>Sélectionne 2 positions dans ta grille pour commencer.</small>
        </div>
        <span className="selection-count" aria-label={`${selected.length} cartes sélectionnées sur 2`}>
          <span className="selection-count__content" aria-hidden="true"><b>{selected.length}</b><span>/2</span></span>
        </span>
        <button className="primary-button primary-button--small" type="button" disabled={selected.length !== 2 || pending || !canSubmit} onClick={onConfirm}>
          {pending ? <span className="button-spinner" /> : <Eye size={16} />}
          Confirmer
        </button>
      </>
    )}
  </motion.aside>
);

const ScoreModal = ({
  game,
  self,
  pending,
  closing,
  canContinue,
  onNextRound,
  onHome,
  onCloseTable,
}: {
  game: GameState;
  self: GamePlayer;
  pending: boolean;
  closing: boolean;
  canContinue: boolean;
  onNextRound: () => Promise<boolean>;
  onHome: () => void;
  onCloseTable: () => Promise<boolean>;
}) => {
  const isGameOver = game.status === "game-ended" || game.phase === "game-ended";
  const resultScores: RoundScore[] =
    (isGameOver ? game.gameResult?.scores : game.roundResult?.scores) ??
    game.players.map((player) => ({
      playerId: player.id,
      playerName: player.name,
      roundScore: player.roundScore ?? 0,
      totalScore: player.totalScore,
      doubled: false,
    }));
  const sortedScores = [...resultScores].sort((a, b) =>
    isGameOver ? a.totalScore - b.totalScore : a.roundScore - b.roundScore,
  );
  const winnerIds = isGameOver
    ? game.gameResult?.winnerPlayerIds ?? (sortedScores[0] ? [sortedScores[0].playerId] : [])
    : sortedScores[0]
      ? sortedScores
          .filter((score) => score.roundScore === sortedScores[0].roundScore)
          .map((score) => score.playerId)
      : [];
  const sharedWin = isGameOver && winnerIds.length > 1;
  const winnerNames = winnerIds
    .map((id) => sortedScores.find((score) => score.playerId === id)?.playerName)
    .filter(Boolean)
    .join(" & ");

  return (
    <motion.div className="score-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <motion.section
        className="score-modal glass-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="score-title"
        initial={{ opacity: 0, y: 26, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 180, damping: 22 }}
      >
        <div className="score-confetti" aria-hidden="true">
          {Array.from({ length: 10 }, (_, index) => <i key={index} />)}
        </div>
        <span className="score-modal__icon">{isGameOver ? <Trophy size={27} /> : <Medal size={25} />}</span>
        <span className="section-kicker">{isGameOver ? "Partie terminée" : `Manche ${game.round} terminée`}</span>
        <h2 id="score-title">
          {isGameOver
            ? winnerIds.includes(self.id)
              ? sharedWin ? "Victoire partagée !" : "Tu remportes la partie !"
              : sharedWin ? `${winnerNames} partagent la victoire` : `${winnerNames || "Un joueur"} remporte la partie`
            : "Les scores sont tombés"}
        </h2>
        <p>{isGameOver ? "Le plus petit total remporte la partie." : "Moins tu marques de points, mieux c’est."}</p>

        <div className="score-table" role="table" aria-label="Scores">
          <div className="score-table__head" role="row">
            <span role="columnheader">Joueur</span><span role="columnheader">Manche</span><span role="columnheader">Total</span>
          </div>
          {sortedScores.map((score, index) => (
            <motion.div
              className={`score-row${score.playerId === self.id ? " is-self" : ""}${winnerIds.includes(score.playerId) ? " is-winner" : ""}`}
              role="row"
              key={score.playerId}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.12 + index * 0.055 }}
            >
              <span role="cell"><b>{index + 1}</b><strong>{score.playerName}{score.playerId === self.id ? " (toi)" : ""}</strong>{winnerIds.includes(score.playerId) && <Crown size={14} />}</span>
              <span role="cell">{score.doubled && <small>x2</small>}{scoreDeltaLabel(score.roundScore)}</span>
              <strong role="cell">{score.totalScore}</strong>
            </motion.div>
          ))}
        </div>

        {isGameOver ? (
          <button className="primary-button primary-button--wide" type="button" onClick={onHome}>
            <Home size={18} /> Retour à l’accueil
          </button>
        ) : self.isHost ? (
          <>
            <button className="primary-button primary-button--wide" type="button" disabled={pending || !canContinue} onClick={() => void onNextRound()}>
              {pending ? <span className="button-spinner" /> : <ChevronRight size={19} />}
              {pending ? "Préparation…" : "Manche suivante"}
            </button>
            <DangerAction
              label="Fermer la table"
              question="Fermer pour tout le monde ?"
              confirmLabel="Fermer"
              icon={<DoorClosed size={16} />}
              pending={closing}
              disabled={pending}
              onConfirm={() => void onCloseTable()}
            />
          </>
        ) : (
          /* Ce panneau recouvre l'en-tête : sans sortie ici, un joueur dont
             l'hôte ne revient jamais n'aurait plus aucun bouton. */
          <>
            <div className="waiting-next" role="status"><span className="waiting-dots"><i /><i /><i /></span> L’hôte prépare la suite…</div>
            <button className="ghost-button score-modal__leave" type="button" onClick={onHome}>
              <Home size={16} /> Quitter la table
            </button>
          </>
        )}
      </motion.section>
    </motion.div>
  );
};

interface GameScreenProps {
  game: GameState;
  connection: ConnectionState;
  pendingAction: string | null;
  onDraw: (source: DrawSource) => Promise<boolean>;
  onSwap: (index: number) => Promise<boolean>;
  onDiscardDrawn: () => Promise<boolean>;
  onReclaimDiscard: () => Promise<boolean>;
  onRevealAfterDiscard: (index: number) => Promise<boolean>;
  onRevealInitial: (positions: [number, number]) => Promise<boolean>;
  onNextRound: () => Promise<boolean>;
  onLeave: () => void;
  onCloseTable: () => Promise<boolean>;
}

type OptimisticSelfCardAction = {
  roomCode: string;
  round: number;
  playerId: string;
  value: number;
} & (
  | {
      kind: "discard";
      canReclaim: boolean;
    }
  | {
      kind: "reclaim";
    }
);

export const GameScreen = ({
  game,
  connection,
  pendingAction,
  onDraw,
  onSwap,
  onDiscardDrawn,
  onReclaimDiscard,
  onRevealAfterDiscard,
  onRevealInitial,
  onNextRound,
  onLeave,
  onCloseTable,
}: GameScreenProps) => {
  const [setupSelection, setSetupSelection] = useState<number[]>([]);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [scoresRevealed, setScoresRevealed] = useState(false);
  const [inspectedPlayerId, setInspectedPlayerId] = useState<string | null>(null);
  /** Groupes joués l'un après l'autre ; les vols d'un même groupe sont simultanés. */
  const [flightQueue, setFlightQueue] = useState<FlightGroup[]>([]);
  const [rememberedOpponentDraw, setRememberedOpponentDraw] = useState<PickupEvent | null>(null);
  const [optimisticSelfAction, setOptimisticSelfAction] = useState<OptimisticSelfCardAction | null>(null);
  const placementActionLock = useRef(false);
  const optimisticSelfActionRef = useRef<OptimisticSelfCardAction | null>(null);
  const previousGameRef = useRef(game);
  const animatedGroupsRef = useRef<string[]>([]);
  const connectionInterruptedRef = useRef(connection !== "connected");
  const reducedMotion = useReducedMotion();
  const optimisticActionIsCurrent = optimisticSelfAction !== null;
  const presentedGame: GameState = optimisticActionIsCurrent
    ? optimisticSelfAction.kind === "discard"
      ? {
          ...game,
          phase: "reveal-after-discard",
          heldCard: null,
          drawnFrom: null,
          discardTop: optimisticSelfAction.value,
          canDiscardHeld: false,
          canReclaimDiscard: optimisticSelfAction.canReclaim,
        }
      : {
          ...game,
          phase: "swap-or-discard",
          heldCard: optimisticSelfAction.value,
          drawnFrom: "discard",
          // La carte du dessous reste inconnue jusqu'au snapshot autoritaire.
          discardTop: null,
          canDiscardHeld: true,
          canReclaimDiscard: false,
        }
    : game;
  const effectivePendingAction =
    pendingAction ?? (optimisticActionIsCurrent ? `optimistic-${optimisticSelfAction.kind}` : null);
  const self = presentedGame.players.find((player) => player.id === presentedGame.selfPlayerId) ?? presentedGame.players[0];
  const opponents = useMemo(
    () => presentedGame.players.filter((player) => player.id !== self?.id).sort((a, b) => a.seat - b.seat),
    [presentedGame.players, self?.id],
  );
  const inspectedPlayer = opponents.find((player) => player.id === inspectedPlayerId) ?? null;
  const activePlayer = presentedGame.players.find((player) => player.id === presentedGame.currentPlayerId);
  const isSetup = presentedGame.phase === "initial-reveal";
  const setupSubmitted = Boolean(self && self.grid.filter((card) => card.state === "revealed").length >= 2);
  /**
   * Ce qui commande la hauteur offerte à la grille : le panneau de mise en place
   * perd une ligne dès que le choix est verrouillé, puis cède entièrement la
   * place à la pioche. Les deux temps redimensionnent les douze cases.
   */
  const gridLayoutKey = `${isSetup}:${setupSubmitted}`;
  const canRevealHidden = Boolean(self?.grid.some((card) => card.state === "hidden"));
  const isSelfTurn = presentedGame.currentPlayerId === presentedGame.selfPlayerId;
  const canDraw = isSelfTurn && presentedGame.phase === "draw" && connection === "connected";
  const canPlace =
    isSelfTurn &&
    presentedGame.phase === "swap-or-discard" &&
    presentedGame.heldCard !== null &&
    connection === "connected";
  const revealAfterDiscard = isSelfTurn && presentedGame.phase === "reveal-after-discard";
  const canRevealAfterDiscard = revealAfterDiscard && connection === "connected";
  const showScores = presentedGame.phase === "round-ended" || presentedGame.phase === "game-ended" || presentedGame.status === "round-ended" || presentedGame.status === "game-ended";
  const canDeriveLiveMotion = connection === "connected" && !connectionInterruptedRef.current;
  const livePlan = canDeriveLiveMotion
    ? planCardMotions(previousGameRef.current, game, { allowRevisionGap: true })
    : EMPTY_PLAN;
  /**
   * Les groupes déduits de ce rendu ne rejoignent la file qu'à l'effet suivant.
   * Les compter ici évite l'image intermédiaire où la grille montre déjà le
   * résultat du coup dont la carte n'a pas encore décollé.
   */
  const plannedGroups = useMemo(
    () => [...flightQueue, ...livePlan.groups],
    [flightQueue, livePlan],
  );
  const activeGroup = flightQueue[0] ?? null;
  const currentGroup = activeGroup ?? livePlan.groups[0] ?? null;
  const covers = useMemo(() => coversOf(plannedGroups), [plannedGroups]);
  const coverOf = useCallback(
    (playerId: string, index: number) => covers.get(`${playerId}:${index}`) ?? null,
    [covers],
  );
  const discardSteps = useMemo(() => discardTimeline(plannedGroups), [plannedGroups]);
  const handArrival = useMemo(() => arrivalOf(plannedGroups, landsInOwnHand), [plannedGroups]);
  const discardArrival = useMemo(() => arrivalOf(plannedGroups, landsOnDiscard), [plannedGroups]);
  const pendingLiveDraw = livePlan.pickup?.isSelf === false ? livePlan.pickup : null;
  const pendingLiveDiscard = livePlan.discard?.isSelf === false ? livePlan.discard : null;
  const activeFlight = activeGroup?.pickup?.isSelf === false ? activeGroup.pickup : null;
  const activeDiscardFlight = activeGroup?.discard?.isSelf === false ? activeGroup.discard : null;
  const visibleDiscardFlight =
    activeDiscardFlight ?? (activeGroup === null ? pendingLiveDiscard : null);
  /**
   * Un coup adverse se regarde : tant qu'une de ses cartes vole, le plateau ne
   * répond plus. Ses propres vols, eux, n'ont rien à faire attendre.
   */
  const interactionLocked = plannedGroups.some((group) =>
    group.flights.some((flight) => flight.role === "opponent"),
  );
  const showScoreModal = showScores && plannedGroups.length === 0;
  const snapshotOpponentDraw =
    activePlayer &&
    game.lastPickup?.playerId === activePlayer.id &&
    game.phase === "swap-or-discard" &&
    game.drawnFrom !== null &&
    game.lastPickup.source === game.drawnFrom
      ? {
          id: game.lastPickup.id,
          sequence: game.lastPickup.sequence,
          revision: game.revision,
          playerId: activePlayer.id,
          playerName: activePlayer.name,
          isSelf: false,
          source: game.lastPickup.source,
          publicValue: game.lastPickup.value,
        }
      : null;
  const matchingRememberedOpponentDraw =
    activePlayer &&
    rememberedOpponentDraw &&
    rememberedOpponentDraw.id === snapshotOpponentDraw?.id &&
    rememberedOpponentDraw.playerId === activePlayer.id &&
    rememberedOpponentDraw.source === game.drawnFrom
      ? rememberedOpponentDraw
      : null;
  const rememberedOpponentDrawQueued =
    matchingRememberedOpponentDraw !== null &&
    activeGroup !== null &&
    activeFlight?.id !== matchingRememberedOpponentDraw.id;
  const pendingOpponentHolding =
    activePlayer &&
    activePlayer.id !== game.selfPlayerId &&
    game.phase === "swap-or-discard" &&
    game.drawnFrom !== null
      ? matchingRememberedOpponentDraw
        ? rememberedOpponentDrawQueued
          ? null
          : matchingRememberedOpponentDraw
        : pendingLiveDraw
          ? null
          : snapshotOpponentDraw ?? {
              id: `pending-${game.round}-${game.revision}-${activePlayer.id}`,
              sequence: game.lastPickup?.sequence ?? game.revision,
              revision: game.revision,
              playerId: activePlayer.id,
              playerName: activePlayer.name,
              isSelf: false,
              source: game.drawnFrom,
              publicValue: null,
            }
      : null;
  const departingOpponentHolding =
    visibleDiscardFlight?.origin === "held" &&
    rememberedOpponentDraw?.playerId === visibleDiscardFlight.playerId &&
    rememberedOpponentDraw.sequence === visibleDiscardFlight.sequence - 1
      ? rememberedOpponentDraw
      : visibleDiscardFlight?.origin === "held"
        ? {
            id: `discard-anchor-${visibleDiscardFlight.id}`,
            sequence: visibleDiscardFlight.sequence,
            revision: visibleDiscardFlight.revision,
            playerId: visibleDiscardFlight.playerId,
            playerName: visibleDiscardFlight.playerName,
            isSelf: false,
            source: "deck" as const,
            publicValue: null,
          }
        : null;
  const opponentHolding = pendingOpponentHolding ?? departingOpponentHolding;
  const drawZonePrompt = visibleDiscardFlight
    ? `${visibleDiscardFlight.playerName} envoie le ${valueLabel(visibleDiscardFlight.value)} à la défausse`
    : activeFlight
      ? `${activeFlight.playerName} prend ${activeFlight.source === "deck" ? "une carte de la pioche" : "la défausse"}`
      : isSelfTurn
        ? presentedGame.phase === "draw"
          ? "Choisis une pile"
          : presentedGame.phase === "reveal-after-discard"
            ? "Révèle une carte cachée"
            : "Joue ta carte"
        : activePlayer
          ? game.phase === "swap-or-discard" && game.drawnFrom !== null
            ? `${activePlayer.name} prend ${(pendingOpponentHolding?.source ?? pendingLiveDraw?.source ?? game.drawnFrom) === "deck" ? "une carte de la pioche" : "la défausse"}`
            : game.phase === "reveal-after-discard"
              ? `${activePlayer.name} choisit une carte à révéler`
              : `${activePlayer.name} joue`
          : "En attente…";

  /**
   * Le fond animé s'arrête tant qu'un panneau le recouvre ou qu'une carte vole :
   * il n'est plus regardé, et son coût tombe pile au moment où l'écran de jeu
   * réclame tout ce que la machine peut donner.
   */
  /**
   * Trois tapes dans le vide puis un maintien : la grille se retourne le temps
   * de l'appui. Le geste ne s'arme pas devant le tableau des scores, où plus
   * rien n'est caché, ni quand un panneau recouvre le plateau.
   */
  const peek = useCheatPeek({
    enabled: !showScores && !rulesOpen && !exitOpen && inspectedPlayer === null,
  });

  useBackdropHold(
    rulesOpen ||
    exitOpen ||
    scoresRevealed ||
    inspectedPlayer !== null ||
    activeGroup !== null,
  );

  /**
   * Rappels d'identité fixe, sans quoi la mémoïsation des cases et des plateaux
   * ne mordrait sur rien. Ils passent par une référence plutôt que par une liste
   * de dépendances : figée à la première closure, elle emprisonnerait `canPlace`,
   * `interactionLocked` et l'action en cours, et laisserait passer une tape qui
   * devrait être refusée.
   */
  const selectCardRef = useRef<(index: number) => void>(() => {});
  const onSelectCard = useCallback((index: number) => selectCardRef.current(index), []);
  const onInspectPlayer = useCallback((playerId: string) => {
    setRulesOpen(false);
    setInspectedPlayerId(playerId);
  }, []);

  /** Un mouvement déjà joué ne se rejoue pas : ni en double, ni au retour. */
  const rememberAnimated = useCallback((groupId: string) => {
    const seen = animatedGroupsRef.current;
    if (!seen.includes(groupId)) seen.push(groupId);
    if (seen.length > 24) seen.splice(0, seen.length - 24);
  }, []);

  const enqueueFlights = useCallback((groups: FlightGroup[]) => {
    setFlightQueue((queue) => [...queue, ...groups]);
  }, []);

  const completeFlight = useCallback((flightId: string) => {
    setFlightQueue((queue) => {
      const [current, ...rest] = queue;
      if (!current) return queue;
      const flights = current.flights.filter((flight) => flight.id !== flightId);
      if (flights.length === current.flights.length) return queue;
      // Le groupe suivant n'entre en piste qu'une fois toutes ses cartes posées.
      return flights.length > 0 ? [{ ...current, flights }, ...rest] : rest;
    });
  }, []);

  useEffect(() => {
    placementActionLock.current = false;
  }, [game.heldCard, game.phase, game.round]);

  useEffect(() => {
    const optimisticAction = optimisticSelfActionRef.current;
    if (!optimisticAction) return;
    const sameTurn =
      game.roomCode === optimisticAction.roomCode &&
      game.round === optimisticAction.round &&
      game.currentPlayerId === optimisticAction.playerId;
    const actionConfirmed =
      optimisticAction.kind === "discard"
        ? game.phase === "reveal-after-discard" &&
          game.heldCard === null &&
          game.discardTop === optimisticAction.value
        : game.phase === "swap-or-discard" &&
          game.heldCard === optimisticAction.value &&
          game.drawnFrom === "discard";
    const sourceStateStillValid =
      optimisticAction.kind === "discard"
        ? game.phase === "swap-or-discard" &&
          game.heldCard === optimisticAction.value &&
          game.canDiscardHeld
        : game.phase === "reveal-after-discard" &&
          game.discardTop === optimisticAction.value &&
          game.canReclaimDiscard;
    if (sameTurn && !actionConfirmed && sourceStateStillValid) return;
    // La carte est partie dès le clic : l'instantané qui rapporte le coup ne
    // doit pas la faire revoler. On reconnaît le mouvement à sa valeur plutôt
    // qu'à la phase atteinte, qui peut déjà être plus loin.
    if (optimisticAction.kind === "discard") {
      const played = game.lastDiscard;
      if (
        played?.playerId === optimisticAction.playerId &&
        played.origin === "held" &&
        played.value === optimisticAction.value
      ) {
        rememberAnimated(`discard-${played.id}`);
      }
    } else {
      const played = game.lastPickup;
      if (
        played?.playerId === optimisticAction.playerId &&
        played.source === "discard" &&
        played.value === optimisticAction.value
      ) {
        rememberAnimated(`pickup-${played.id}`);
      }
    }
    optimisticSelfActionRef.current = null;
    setOptimisticSelfAction(null);
  }, [game, rememberAnimated]);

  useEffect(() => {
    if (!canPlace && !canRevealAfterDiscard) placementActionLock.current = false;
  }, [canPlace, canRevealAfterDiscard]);

  useEffect(() => {
    if (!isSetup) setSetupSelection([]);
  }, [isSetup]);

  useEffect(() => {
    // Une nouvelle donne balaie les vols de la manche précédente : leurs cases
    // n'existent plus, et rien ne doit recouvrir les cartes fraîchement posées.
    setFlightQueue((queue) => (queue.length === 0 ? queue : []));
  }, [game.round]);

  useEffect(() => {
    if (!rulesOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setRulesOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [rulesOpen]);

  useEffect(() => {
    if (inspectedPlayerId !== null && inspectedPlayer === null) {
      setInspectedPlayerId(null);
    }
  }, [inspectedPlayer, inspectedPlayerId]);

  useEffect(() => {
    if (showScores) setInspectedPlayerId(null);
  }, [showScores]);

  useEffect(() => {
    if (!showScoreModal) {
      setScoresRevealed(false);
      return;
    }
    const timer = window.setTimeout(() => setScoresRevealed(true), ROUND_SUMMARY_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [showScoreModal]);

  useEffect(() => {
    if (connection !== "connected") connectionInterruptedRef.current = true;
  }, [connection]);

  useEffect(() => {
    previousGameRef.current = game;
    if (connectionInterruptedRef.current) {
      if (connection === "connected") connectionInterruptedRef.current = false;
      return;
    }

    const fresh = livePlan.groups.filter(
      (group) => !animatedGroupsRef.current.includes(group.id),
    );
    if (fresh.length === 0) return;
    fresh.forEach((group) => rememberAnimated(group.id));

    if (livePlan.pickup && !livePlan.pickup.isSelf) setRememberedOpponentDraw(livePlan.pickup);
    if (livePlan.discard && !livePlan.discard.isSelf) {
      // Un panneau ouvert masquerait le plateau que la carte traverse.
      setRulesOpen(false);
      setInspectedPlayerId(null);
    }
    enqueueFlights(fresh);
  }, [connection, enqueueFlights, game, livePlan, rememberAnimated]);

  useEffect(() => {
    if (plannedGroups.length > 0) return;
    if (!game.currentPlayerId || game.currentPlayerId === self?.id) return;
    const board = document.querySelector<HTMLElement>(
      `.opponent-board[data-player-id="${CSS.escape(game.currentPlayerId)}"]`,
    );
    const strip = board?.closest<HTMLElement>(".opponents-strip");
    if (!board || !strip) return;

    // La bande est peut-être déjà au bon endroit : réécrire la même position
    // interromprait un défilement lissé en cours et la ferait sauter.
    const target = stripScrollTarget(strip, board);
    if (target === null) return;
    strip.scrollTo({ left: target, behavior: reducedMotion ? "auto" : "smooth" });
  }, [game.currentPlayerId, plannedGroups, reducedMotion, self?.id]);

  if (!self) return null;

  const rollbackOptimisticSelfAction = (action: OptimisticSelfCardAction) => {
    if (optimisticSelfActionRef.current !== action) return;
    optimisticSelfActionRef.current = null;
    setOptimisticSelfAction(null);
  };

  const discardDrawnImmediately = async () => {
    if (
      optimisticSelfActionRef.current !== null ||
      pendingAction !== null ||
      connection !== "connected" ||
      game.currentPlayerId !== game.selfPlayerId ||
      game.phase !== "swap-or-discard" ||
      game.heldCard === null ||
      !game.canDiscardHeld ||
      !canRevealHidden
    ) {
      return false;
    }

    const action: OptimisticSelfCardAction = {
      kind: "discard",
      roomCode: game.roomCode,
      round: game.round,
      playerId: game.selfPlayerId,
      value: game.heldCard,
      canReclaim: game.drawnFrom === "deck",
    };
    optimisticSelfActionRef.current = action;
    // Le vol part du clic, pas de l'instantané : sans cela la carte quitterait
    // la main avant qu'on ne la voie rejoindre la défausse.
    enqueueFlights([
      localDiscardGroup({
        playerId: game.selfPlayerId,
        value: game.heldCard,
        previousDiscardTop: game.discardTop,
        token: `${game.round}-${game.revision}`,
      }),
    ]);
    setOptimisticSelfAction(action);
    const succeeded = await onDiscardDrawn();
    if (!succeeded) rollbackOptimisticSelfAction(action);
    return succeeded;
  };

  const reclaimDiscardImmediately = async () => {
    if (
      optimisticSelfActionRef.current !== null ||
      pendingAction !== null ||
      connection !== "connected" ||
      game.currentPlayerId !== game.selfPlayerId ||
      game.phase !== "reveal-after-discard" ||
      !game.canReclaimDiscard ||
      game.discardTop === null
    ) {
      return false;
    }

    const action: OptimisticSelfCardAction = {
      kind: "reclaim",
      roomCode: game.roomCode,
      round: game.round,
      playerId: game.selfPlayerId,
      value: game.discardTop,
    };
    optimisticSelfActionRef.current = action;
    enqueueFlights([
      localReclaimGroup({
        playerId: game.selfPlayerId,
        value: game.discardTop,
        token: `${game.round}-${game.revision}`,
      }),
    ]);
    setOptimisticSelfAction(action);
    const succeeded = await onReclaimDiscard();
    if (!succeeded) rollbackOptimisticSelfAction(action);
    return succeeded;
  };

  const submitPlacement = async (action: "swap" | "reveal", index: number) => {
    const card = self.grid[index];
    const actionAllowed = action === "reveal" ? canRevealAfterDiscard : canPlace;
    if (
      placementActionLock.current ||
      effectivePendingAction ||
      !actionAllowed ||
      !card ||
      card.state === "removed" ||
      (action === "reveal" && card.state !== "hidden")
    ) {
      return;
    }

    placementActionLock.current = true;
    const succeeded =
      action === "reveal"
        ? await onRevealAfterDiscard(index)
        : await onSwap(index);
    if (!succeeded) placementActionLock.current = false;
  };

  const selectCard = (index: number) => {
    const card = self.grid[index];
    if (
      !card ||
      card.state === "removed" ||
      effectivePendingAction ||
      interactionLocked ||
      connection !== "connected"
    ) {
      return;
    }
    if (isSetup && !setupSubmitted) {
      if (card.state !== "hidden") return;
      setSetupSelection((current) =>
        current.includes(index)
          ? current.filter((position) => position !== index)
          : current.length < 2
            ? [...current, index]
            : [current[1], index],
      );
      return;
    }
    if (canRevealAfterDiscard) {
      if (card.state !== "hidden") return;
      void submitPlacement("reveal", index);
      return;
    }
    if (!canPlace) return;
    void submitPlacement("swap", index);
  };

  // Le rappel stable remis à jour à chaque rendu : les cases gardent une prop
  // `onSelect` immuable tout en jouant toujours la dernière logique.
  selectCardRef.current = selectCard;

  const cardInteractive = (card: CardView) => {
    if (effectivePendingAction || interactionLocked || connection !== "connected") return false;
    if (isSetup && !setupSubmitted) return card.state === "hidden";
    if (canRevealAfterDiscard) return card.state === "hidden";
    if (!canPlace) return false;
    return card.state !== "removed";
  };

  const placementCardLabel = (card: CardView, index: number) => {
    const position = `ligne ${Math.floor(index / 4) + 1}, colonne ${(index % 4) + 1}`;
    if (canRevealAfterDiscard) {
      return card.state === "hidden" ? `Carte cachée, ${position}, révéler cette carte` : undefined;
    }
    if (!canPlace || card.state === "removed" || presentedGame.heldCard === null) return undefined;
    return card.state === "hidden"
      ? `Carte cachée, ${position}, la remplacer par le ${valueLabel(presentedGame.heldCard)}`
      : `Carte ${card.value}, ${position}, la remplacer par le ${valueLabel(presentedGame.heldCard)}`;
  };

  const turnMessage = isSetup
    ? setupSubmitted
      ? "Choix verrouillé — les autres révèlent leurs cartes"
      : "Choisis deux cartes à révéler"
    : isSelfTurn
      ? presentedGame.phase === "draw"
        ? "À toi — choisis la pioche ou la défausse"
        : presentedGame.phase === "swap-or-discard"
          ? presentedGame.heldCard !== null
            ? presentedGame.canDiscardHeld && canRevealHidden
              ? `Place le ${valueLabel(presentedGame.heldCard)} dans ta grille ou défausse-le`
              : `Place le ${valueLabel(presentedGame.heldCard)} dans ta grille`
            : "Choisis une carte de ta grille"
          : presentedGame.phase === "reveal-after-discard"
            ? "Choisis une carte cachée à révéler"
          : "Ton coup se résout…"
      : presentedGame.phase === "reveal-after-discard"
        ? `${activePlayer?.name ?? "Un joueur"} choisit une carte à révéler…`
        : `${activePlayer?.name ?? "Un joueur"} réfléchit…`;

  return (
    <main
      className="game-shell"
      // Le geste se lit sur toute la coque, mais ne retient que les appuis
      // tombés à côté de ce qui se manipule : le tri se fait dans le crochet.
      {...peek.handlers}
      // Les repères CSS qui doivent s'allumer à l'arrivée d'une carte tiennent
      // leur retard des durées de vol, pour qu'un seul réglage fasse foi.
      style={{ "--flight-landing-opponent": `${landingAfter("opponent")}s` } as CSSProperties}
    >
      <header className="game-header glass-panel">
        <div className="game-header__brand"><Brand compact /></div>
        <div className="game-meta">
          <span><small>Table</small><strong>{game.roomCode}</strong></span>
          <i />
          <span><small>Manche</small><strong>{game.round}</strong></span>
        </div>
        <div className="game-header__actions">
          <button
            className="icon-button"
            type="button"
            aria-label="Afficher les règles rapides"
            aria-expanded={rulesOpen}
            onClick={() => setRulesOpen(true)}
            title="Règles rapides"
          >
            <CircleHelp size={18} />
          </button>
          {/* Une partie en cours ne se quitte pas d'une tape de travers : ce
              bouton pose la question au lieu d'exécuter. */}
          <button
            className="icon-button"
            type="button"
            onClick={() => setExitOpen(true)}
            aria-label="Quitter la table"
            aria-expanded={exitOpen}
            title="Quitter la table"
          >
            <Home size={18} />
          </button>
        </div>
      </header>

      <section className={`opponents-strip${opponents.length === 1 ? " is-single" : ""}`} aria-label="Autres joueurs">
        {opponents.map((player) => (
          <OpponentBoard
            key={player.id}
            player={player}
            active={!isSetup && player.id === game.currentPlayerId}
            holding={opponentHolding?.playerId === player.id ? opponentHolding : null}
            receiving={activeFlight?.playerId === player.id && !reducedMotion}
            discarding={visibleDiscardFlight?.playerId === player.id ? visibleDiscardFlight : null}
            covers={covers}
            onInspect={onInspectPlayer}
          />
        ))}
      </section>

      <section className={`table-center${isSetup ? " is-setup" : ""}`}>
        <div className={`turn-banner${isSelfTurn && !isSetup ? " is-your-turn" : ""}`} role="status" aria-live="polite">
          <span className="turn-banner__dot"><i /></span>
          <strong>{turnMessage}</strong>
          {game.lastAction && <small>{game.lastAction}</small>}
        </div>

        {isSetup ? (
          <SetupPanel
            selected={setupSelection}
            submitted={setupSubmitted}
            pending={pendingAction === "initial-reveal"}
            canSubmit={connection === "connected"}
            onConfirm={() => {
              if (setupSelection.length === 2 && connection === "connected") {
                void onRevealInitial([setupSelection[0], setupSelection[1]]);
              }
            }}
          />
        ) : (
          <DrawZone
            game={presentedGame}
            canDraw={canDraw}
            canPlace={canPlace}
            canRevealHidden={canRevealHidden}
            revealAfterDiscard={revealAfterDiscard}
            canRevealAfterDiscard={canRevealAfterDiscard}
            isSelfTurn={isSelfTurn}
            prompt={drawZonePrompt}
            pendingAction={effectivePendingAction}
            interactionLocked={interactionLocked}
            flightSource={currentGroup?.pickup?.source ?? null}
            discardArrival={discardArrival}
            discardSteps={discardSteps}
            handArrival={handArrival}
            incomingDiscard={visibleDiscardFlight}
            onDiscardDrawn={discardDrawnImmediately}
            onReclaimDiscard={reclaimDiscardImmediately}
            onDraw={onDraw}
          />
        )}
      </section>

      <section className={`own-board${isSelfTurn && !isSetup ? " is-active" : ""}${revealAfterDiscard ? " is-reveal-choice" : ""}`} aria-labelledby="own-board-title">
        <header className="own-board__head">
          <div className="own-player">
            <span
              className={`tiny-avatar${self.account ? " tiny-avatar--account" : ""}`}
              style={avatarStyle(self.account, self.seat)}
            >
              {self.account ? self.account.displayName.slice(0, 1).toUpperCase() : <UserRound size={15} />}
              <AvatarPhoto profile={self.account} />
            </span>
            <span><strong id="own-board-title">Ta grille</strong><small>{self.name}</small></span>
          </div>
          <div className="own-score"><small>Total</small><strong>{self.totalScore}</strong><span>pts</span></div>
        </header>
        <div className="card-grid" role="group" aria-label="Ta grille de douze cartes">
          {self.grid.map((card, index) => (
            <NumberCard
              key={index}
              cardState={card.state}
              cardValue={card.value}
              peekValue={peek.peeking ? card.secret ?? null : null}
              index={index}
              interactive={cardInteractive(card)}
              selected={setupSelection.includes(index)}
              actionHint={
                canRevealAfterDiscard
                  ? card.state === "hidden" ? "reveal" : undefined
                  : canPlace && card.state !== "removed"
                    ? "swap"
                    : undefined
              }
              cover={coverOf(self.id, index)}
              layoutKey={gridLayoutKey}
              onSelect={onSelectCard}
              label={placementCardLabel(card, index)}
            />
          ))}
        </div>
      </section>

      {connection !== "connected" && (
        <div className="reconnect-banner" role="status">
          <WifiOff size={16} /> Connexion perdue — on garde ta place au chaud…
        </div>
      )}

      <CardFlightLayer flights={activeGroup?.flights ?? []} onComplete={completeFlight} />

      <AnimatePresence>
        {inspectedPlayer && (
          <OpponentInspector
            key={inspectedPlayer.id}
            player={inspectedPlayer}
            active={!isSetup && inspectedPlayer.id === game.currentPlayerId}
            onClose={() => setInspectedPlayerId(null)}
          />
        )}
        {rulesOpen && (
          <motion.div
            className="rules-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={() => setRulesOpen(false)}
          >
            <motion.section
              className="rules-modal glass-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="rules-title"
              initial={{ opacity: 0, y: 18, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <header>
                <span className="rules-modal__icon"><Grid3X3 size={21} /></span>
                <div><span className="section-kicker">Comment jouer</span><h2 id="rules-title">Règles rapides</h2></div>
                <button className="icon-button" type="button" autoFocus onClick={() => setRulesOpen(false)} aria-label="Fermer les règles"><X size={18} /></button>
              </header>
              <ol className="rules-list">
                <li><b>1</b><span><strong>Vise le plus bas</strong><small>À la fin, toutes les valeurs encore dans ta grille sont additionnées.</small></span></li>
                <li><b>2</b><span><strong>Pioche puis décide</strong><small>Échange la carte tenue, ou défausse une carte venue de la pioche pour révéler une case cachée.</small></span></li>
                <li><b>3</b><span><strong>Aligne une colonne</strong><small>Trois valeurs identiques révélées dans une colonne verticale disparaissent du score.</small></span></li>
                <li><b>4</b><span><strong>Ferme au bon moment</strong><small>Révéler toute ta grille déclenche le dernier tour. Si tu n’as pas seul le plus petit score, tes points positifs sont doublés.</small></span></li>
              </ol>
              <p className="rules-modal__end"><Trophy size={15} /> La partie s’arrête dès qu’un total atteint 100 ; le plus petit total gagne.</p>
              <button className="secondary-button rules-modal__close" type="button" onClick={() => setRulesOpen(false)}>Compris !</button>
            </motion.section>
          </motion.div>
        )}
        {exitOpen && (
          <motion.div
            className="rules-backdrop exit-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={() => setExitOpen(false)}
          >
            <motion.section
              className="exit-modal glass-panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="exit-title"
              initial={{ opacity: 0, y: 18, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <span className="section-kicker section-kicker--danger">
                <DoorOpen size={14} /> Sortie
              </span>
              <h2 id="exit-title">Quitter la partie ?</h2>
              <p>
                Ta place est perdue : le tour de rôle t’enjambera et ton score restera
                acquis aux manches déjà jouées.
              </p>
              <div className="exit-modal__actions">
                <button
                  className="secondary-button secondary-button--danger"
                  type="button"
                  autoFocus
                  onClick={onLeave}
                >
                  <Home size={16} /> Quitter la table
                </button>
                {self.isHost && (
                  <DangerAction
                    label="Fermer la table pour tout le monde"
                    question="Fermer pour tout le monde ?"
                    confirmLabel="Fermer"
                    icon={<DoorClosed size={16} />}
                    pending={pendingAction === "close-room"}
                    onConfirm={() => void onCloseTable()}
                  />
                )}
                <button
                  className="ghost-button"
                  type="button"
                  onClick={() => setExitOpen(false)}
                >
                  Reprendre la partie
                </button>
              </div>
            </motion.section>
          </motion.div>
        )}
        {scoresRevealed && (
          <ScoreModal
            game={game}
            self={self}
            pending={pendingAction === "next-round"}
            closing={pendingAction === "close-room"}
            canContinue={connection === "connected"}
            onNextRound={onNextRound}
            onHome={onLeave}
            onCloseTable={onCloseTable}
          />
        )}
      </AnimatePresence>
    </main>
  );
};
